// client/src/pages/ownerListings/OwnerListingsPage.js — the owner's dashboard: my listings
// (draft / in review / live / paused / leased / sold ...), views and saves from the site's
// own engagement events, inquiries and showing requests (contact details only as the
// brokerage allows), masked messages, the listing agreement to sign, and status actions.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ownerApi, btn, btn2, inputCls, STATUS_CHIP } from '../../utils/ownerListingsApi';

const when = (s) => (s ? new Date(s).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '');
const day = (s) => (s ? new Date(s).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '');

const VerifyBox = ({ me, reload }) => {
    const [msg, setMsg] = useState('');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [sent, setSent] = useState(false);
    const act = async (fn) => { setMsg(''); try { const r = await fn(); setMsg(r.message || 'Done.'); reload(); return r; } catch (e) { setMsg(e.message); return null; } };
    return (
        <section className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-950" aria-label="Verify your account">
            {!me.email_verified && (
                <div>
                    <p className="font-semibold">Confirm your email address</p>
                    <p className="text-sm">We sent a link to {me.email}. You can prepare a listing now; it can be submitted once your email is confirmed.</p>
                    <button type="button" className={`${btn2} mt-2`} onClick={() => act(() => ownerApi('/resend-verification', { method: 'POST' }))}>Send it again</button>
                </div>
            )}
            <div>
                <p className="font-semibold">Verify your mobile number (recommended)</p>
                <p className="text-sm">Verified owners are trusted faster. We text you a 6-digit code.</p>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                    <label className="text-sm">Mobile number<input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" /></label>
                    <button type="button" className={btn2} onClick={async () => { const r = await act(() => ownerApi('/phone/request', { method: 'POST', body: { phone } })); if (r && r.sent) setSent(true); }}>Text me a code</button>
                    {sent && <>
                        <label className="text-sm">Code<input className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" /></label>
                        <button type="button" className={btn2} onClick={() => act(() => ownerApi('/phone/confirm', { method: 'POST', body: { code } }))}>Verify</button>
                    </>}
                </div>
            </div>
            {msg && <p className="text-sm" role="status">{msg}</p>}
        </section>
    );
};

const Thread = ({ inquiryId }) => {
    const [t, setT] = useState(null);
    const [body, setBody] = useState('');
    const [err, setErr] = useState('');
    const load = useCallback(() => ownerApi(`/inquiries/${inquiryId}/messages`).then(setT).catch((e) => setErr(e.message)), [inquiryId]);
    useEffect(() => { load(); }, [load]);
    const send = async (e) => {
        e.preventDefault(); setErr('');
        try { await ownerApi(`/inquiries/${inquiryId}/messages`, { method: 'POST', body: { body } }); setBody(''); load(); } catch (x) { setErr(x.message); }
    };
    if (!t) return <p className="text-sm text-slate-600">{err || 'Loading…'}</p>;
    return (
        <div className="mt-2 rounded-md bg-slate-50 p-3">
            <ul className="space-y-2">
                {t.messages.map((m) => (
                    <li key={m.message_id} className={`rounded p-2 text-sm ${m.sender_type === 'owner' ? 'bg-indigo-50' : 'bg-white'}`}>
                        <span className="font-semibold">{m.sender_type === 'owner' ? 'You' : m.sender_type === 'staff' ? 'Our office' : t.inquiry.person_name}</span>
                        <span className="ml-2 text-xs text-slate-500">{when(m.created_at)}</span>
                        <p className="whitespace-pre-line">{m.body}</p>
                    </li>
                ))}
                {!t.messages.length && <li className="text-sm text-slate-600">No messages yet.</li>}
            </ul>
            <form onSubmit={send} className="mt-2">
                <label className="sr-only" htmlFor={`msg-${inquiryId}`}>Message</label>
                <textarea id={`msg-${inquiryId}`} rows={2} className={inputCls} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write a reply. Your email and phone are not shown." />
                {err && <p className="text-sm text-red-700" role="alert">{err}</p>}
                <button type="submit" className={`${btn} mt-2`} disabled={!body.trim()}>Send</button>
                <p className="mt-1 text-xs text-slate-600">Messages are checked for fair-housing wording and sent through our office.</p>
            </form>
        </div>
    );
};

const ListingPanel = ({ l, reload }) => {
    const [inq, setInq] = useState(null);
    const [openThread, setOpenThread] = useState(null);
    const [msg, setMsg] = useState('');
    const [weeks, setWeeks] = useState(null);
    useEffect(() => { ownerApi(`/listings/${l.listing_id}/analytics`).then(setWeeks).catch(() => setWeeks([])); }, [l.listing_id]);
    const loadInq = useCallback(() => ownerApi(`/listings/${l.listing_id}/inquiries`).then(setInq).catch(() => setInq({ inquiries: [] })), [l.listing_id]);
    useEffect(() => { loadInq(); }, [loadInq]);
    const action = async (a, confirmText) => {
        if (confirmText && !window.confirm(confirmText)) return;
        setMsg('');
        try { const r = await ownerApi(`/listings/${l.listing_id}/action`, { method: 'POST', body: { action: a } }); setMsg(r.message || 'Done.'); reload(); } catch (e) { setMsg(e.message); }
    };
    const sign = async () => {
        try { const r = await ownerApi(`/listings/${l.listing_id}/agreement/sign-link`, { method: 'POST' }); window.location.href = r.url; } catch (e) { setMsg(e.message); }
    };
    const decide = async (q, decision, slot) => {
        try { await ownerApi(`/inquiries/${q.inquiry_id}/decide`, { method: 'POST', body: { decision, slot } }); loadInq(); } catch (e) { setMsg(e.message); }
    };
    const st = l.listing_status;
    return (
        <div className="mt-3 space-y-3 border-t border-slate-200 pt-3">
            <div className="flex flex-wrap gap-2">
                {l.editable && <Link className={btn2} to={`/client-portal/my-listings/${l.listing_id}`}>{l.public_path ? 'Edit (goes back to review)' : 'Edit'}</Link>}
                {st === 'approved_pending_agreement' && <button type="button" className={btn} onClick={sign}>Sign the listing agreement</button>}
                {st === 'live' && <button type="button" className={btn2} onClick={() => action('pause')}>Pause</button>}
                {st === 'paused' && <button type="button" className={btn2} onClick={() => action('resume')}>Resume</button>}
                {['live', 'paused'].includes(st) && l.purpose === 'lease' && <button type="button" className={btn2} onClick={() => action('leased', 'Mark this property as leased? It will show as Rented.')}>Mark leased</button>}
                {['live', 'paused'].includes(st) && l.purpose === 'sale' && <button type="button" className={btn2} onClick={() => action('sold', 'Mark this property as sold?')}>Mark sold</button>}
                {['live', 'paused', 'expired'].includes(st) && <button type="button" className={btn2} onClick={() => action('renew')}>Ask to renew</button>}
                {!['leased', 'sold', 'rejected', 'withdrawn'].includes(st) && st !== 'draft' && <button type="button" className={btn2} onClick={() => action('withdraw', 'Withdraw this listing? It comes off the website.')}>Withdraw</button>}
                {['live', 'paused'].includes(st) && l.purpose === 'lease' && <button type="button" className={btn2} onClick={() => action('request_management', 'Ask our property management team to contact you about managing this property?')}>Have the brokerage manage it</button>}
                <button type="button" className={btn2} onClick={() => action('delete', 'Remove this listing from your list? If it was ever submitted, our office keeps its record for the period the law requires.')}>Delete</button>
            </div>
            {msg && <p className="text-sm text-slate-800" role="status">{msg}</p>}
            {weeks && weeks.length > 0 && (
                <section aria-label="Weekly activity">
                    <h3 className="font-semibold text-slate-900">Activity by week</h3>
                    <table className="mt-1 text-sm"><thead><tr className="text-left text-slate-600"><th className="pr-4">Week of</th><th className="pr-4">Views</th><th className="pr-4">Saves</th><th className="pr-4">Inquiries</th><th>Showing requests</th></tr></thead>
                        <tbody>{weeks.map((w) => <tr key={w.week}><td className="pr-4">{w.week}</td><td className="pr-4">{w.views}</td><td className="pr-4">{w.saves}</td><td className="pr-4">{w.inquiries}</td><td>{w.showings}</td></tr>)}</tbody></table>
                </section>
            )}
            <section aria-label="Inquiries">
                <h3 className="font-semibold text-slate-900">Inquiries and showing requests</h3>
                {inq && !inq.contact_shared && <p className="text-xs text-slate-600">Contact details stay with our office; reply through the messages below.</p>}
                {inq && inq.inquiries.length === 0 && <p className="text-sm text-slate-600">None yet.</p>}
                <ul className="mt-2 space-y-2">
                    {(inq ? inq.inquiries : []).map((q) => (
                        <li key={q.inquiry_id} className="rounded-md border border-slate-200 p-3 text-sm">
                            <p><span className="font-semibold">{q.person_name}</span> · {q.inquiry_kind === 'showing' ? 'Showing request' : q.inquiry_kind === 'application' ? 'Application' : 'Question'} · {when(q.created_at)} · <span className="font-medium">{q.inquiry_status}</span></p>
                            {q.contact_shared && (q.email || q.phone) && <p className="text-slate-700">{[q.email, q.phone].filter(Boolean).join(' · ')}</p>}
                            {q.message && <p className="mt-1 whitespace-pre-line text-slate-800">{q.message}</p>}
                            {q.inquiry_kind === 'showing' && q.inquiry_status === 'new' && inq.owner_confirms_showings && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {q.slots.map((s) => <button key={s} type="button" className={btn2} onClick={() => decide(q, 'confirm', s)}>Confirm {when(s)}</button>)}
                                    <button type="button" className={btn2} onClick={() => decide(q, 'decline')}>None of these work</button>
                                </div>
                            )}
                            {q.confirmed_slot && <p className="mt-1 text-emerald-800">Confirmed for {when(q.confirmed_slot)}. A licensee from our office attends.</p>}
                            <button type="button" className="mt-2 text-indigo-800 underline" onClick={() => setOpenThread(openThread === q.inquiry_id ? null : q.inquiry_id)}>
                                {openThread === q.inquiry_id ? 'Hide messages' : 'Messages'}</button>
                            {openThread === q.inquiry_id && <Thread inquiryId={q.inquiry_id} />}
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
};

const OwnerListingsPage = () => {
    const loc = useLocation();
    const [me, setMe] = useState(null);
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(null);
    const [error, setError] = useState('');
    const load = useCallback(async () => {
        try {
            const m = await ownerApi('/me');
            setMe(m);
            if (m.is_owner) setRows(await ownerApi('/listings'));
        } catch (e) { setError(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const enable = async () => { try { await ownerApi('/enable', { method: 'POST' }); load(); } catch (e) { setError(e.message); } };

    if (!me) return <p className="text-slate-700" role="status">{error || 'Loading…'}</p>;
    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-bold text-slate-900">My Listings</h1>
                {me.is_owner && me.enabled && <Link to="/client-portal/my-listings/new" className={btn}>List a property</Link>}
            </div>
            {loc.state && loc.state.welcome && <p className="rounded-md bg-emerald-50 p-3 text-emerald-900" role="status">{loc.state.welcome}</p>}
            {error && <p className="text-red-700" role="alert">{error}</p>}
            {!me.enabled && <p className="rounded-md bg-slate-200 p-3 text-slate-800">Online listing is not open on this site right now. Please call our office.</p>}
            {!me.is_owner && me.enabled && (
                <section className="rounded-lg bg-white p-6 shadow">
                    <h2 className="text-lg font-semibold text-slate-900">Own a property you want to sell or lease?</h2>
                    <p className="mt-1 text-slate-700">List it with our brokerage from here. A licensee reviews every listing before it is published.</p>
                    <button type="button" className={`${btn} mt-3`} onClick={enable}>List my property</button>
                </section>
            )}
            {me.is_owner && <VerifyBox me={me} reload={load} />}
            {me.is_owner && me.settings.fees_text && (
                <section className="rounded-lg bg-white p-4 text-sm text-slate-800 shadow"><h2 className="font-semibold">Our fees</h2><p className="whitespace-pre-line">{me.settings.fees_text}</p></section>
            )}
            {me.is_owner && (
                <ul className="space-y-3">
                    {rows.length === 0 && <li className="rounded-lg bg-white p-4 text-slate-700 shadow">No listings yet.</li>}
                    {rows.map((l) => (
                        <li key={l.listing_id} className="rounded-lg bg-white p-4 shadow">
                            <div className="flex flex-wrap items-start justify-between gap-2">
                                <div>
                                    <p className="font-semibold text-slate-900">{l.title_line || `Listing #${l.listing_id}`}</p>
                                    <p className="text-sm text-slate-700">{l.purpose === 'lease' ? 'For lease' : 'For sale'} · {l.category.replace('_', ' ')}</p>
                                    <p className="mt-1 flex flex-wrap items-center gap-2 text-sm">
                                        <span className={`rounded px-2 py-0.5 font-semibold ${STATUS_CHIP[l.listing_status] || ''}`}>{l.status_label}</span>
                                        {l.revision_status !== 'none' && <span className="rounded bg-amber-100 px-2 py-0.5 text-amber-900">Changes: {l.revision_status.replace('_', ' ')}</span>}
                                        {l.expires_at && <span className="text-slate-600">Runs until {day(l.expires_at)}</span>}
                                    </p>
                                    {l.review_note && <p className="mt-1 text-sm text-orange-900">Note from our office: {l.review_note}</p>}
                                </div>
                                <div className="text-right text-sm text-slate-700">
                                    {l.views && <p>{l.views.views} views · {l.views.visitors} visitors · {l.views.favorites} saves</p>}
                                    {l.views && <p className="text-xs">{l.views.views_7d} views in the last 7 days</p>}
                                    {l.new_inquiries > 0 && <p className="font-semibold text-indigo-900">{l.new_inquiries} new inquir{l.new_inquiries === 1 ? 'y' : 'ies'}</p>}
                                    {l.public_path && <Link className="text-indigo-800 underline" to={l.public_path}>View on the website</Link>}
                                </div>
                            </div>
                            <button type="button" className="mt-2 text-sm font-semibold text-indigo-800 underline" aria-expanded={open === l.listing_id}
                                onClick={() => setOpen(open === l.listing_id ? null : l.listing_id)}>{open === l.listing_id ? 'Close' : 'Manage'}</button>
                            {open === l.listing_id && <ListingPanel l={l} reload={load} />}
                        </li>
                    ))}
                </ul>
            )}
            {me.is_owner && <p className="text-xs text-slate-600">{me.settings.ad_rule}</p>}
        </div>
    );
};
export default OwnerListingsPage;
